import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Clock, ArrowLeft, AlertTriangle, Share2, Copy, Check,
  ExternalLink, Linkedin, Twitter
} from 'lucide-react';
import { supabase } from '../services/supabase';

import ArticleRenderer from '../components/article/ArticleRenderer';
import MermaidChart from '../components/article/MermaidChart';
import {
  ReadingProgressBar,
  TOCSidebar,
  BiasBadge,
  ScenarioMatrix,
  KeyLevelsBox,
  SignalCTABanner,
  AuthorBio,
  FAQAccordion,
  RelatedPosts,
  SidebarMacroBiasCard,
  SidebarCTA,
  ArticleMeta,
} from '../components/article/ArticleComponents';
import { useSSRData } from '@/src/context/SSRContext';

// ─────────────────────────────────────────────────────────────────────────
// SEO Overrides for Specific Articles
// ─────────────────────────────────────────────────────────────────────────
const SLUG_METADATA_OVERRIDES: Record<string, { title: string; description: string }> = {
  'why-is-my-gold-strategy-failing-today': {
    title: 'Why Your Gold Strategy Fails — Stop Being Institutional Exit Liquidity',
    description: 'Gold trades fail because retail strategies ignore institutional liquidity cascades. Learn how XAUUSD order blocks, DXY macro drivers, and hidden liquidity pools hunt your stops. Stop trading blind.'
  }
};

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function hasContent(val: any): boolean {
  if (val == null) return false;
  if (typeof val === 'string') return val.trim().length > 0;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
}

const cleanArticleContent = (content: string) => {
  if (!content) return '';
  return content
    // Standardize to www
    .replace(/https?:\/\/forexfundai\.com(?!\.com)/g, 'https://www.forexfundai.com')
    // Fix legacy paths
    .replace(/href="\/risk-disclaimer"/g, 'href="/disclaimer"')
    .replace(/href="\/blog"/g, 'href="/insights"')
    // REMOVE dead links (don't redirect) as per user request
    .replace(/<a[^>]*href=["']\/?a\/?["'][^>]*>(.*?)<\/a>/gi, '$1')
    .replace(/<img[^>]*src=["'][^"']*team-avatar\.png["'][^>]*>/gi, '')
    // Remove dead links but keep the text for other legacy paths
    .replace(/<a[^>]*href="[^"]*(academy|strategies|assets\/team-avatar\.png|(?<![a-zA-Z0-9])\/r(?![a-zA-Z0-9])|(?<![a-zA-Z0-9])\/s(?![a-zA-Z0-9]))[^"]*"[^>]*>(.*?)<\/a>/gi, '$2')
    // Fix any remaining raw links to strategies/academy
    .replace(/https?:\/\/www\.forexfundai\.com\/(academy|strategies)/g, '/');
};

// ─────────────────────────────────────────────────────────────────────────
// Reading Progress Hook
// ─────────────────────────────────────────────────────────────────────────

function useReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.getElementById('article-body');
      if (!el) return;
      const { top, height } = el.getBoundingClientRect();
      const scrollable = height - window.innerHeight;
      if (scrollable <= 0) { setProgress(100); return; }
      setProgress(Math.round(Math.min(100, Math.max(0, (-top / scrollable) * 100))));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return progress;
}

// ─────────────────────────────────────────────────────────────────────────
// Active Heading Hook — scroll-spy for TOC
// ─────────────────────────────────────────────────────────────────────────

function useActiveHeading(toc: any[] | null) {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (!toc || toc.length === 0) return;

    const ids = toc.map((t: any) => t.id);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 }
    );

    ids.forEach((id: string) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [toc]);

  return activeId;
}

// ─────────────────────────────────────────────────────────────────────────
// Social Share Bar Component
// ─────────────────────────────────────────────────────────────────────────

function SocialShareBar({ title, url }: { title: string; url: string }) {
  const [copied, setCopied] = useState(false);

  const copyLink = useCallback(() => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

  return (
    <div className="flex flex-wrap items-center gap-4 mt-6 py-4 border-y border-gray-800">
      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-400">
        <Share2 className="w-4 h-4" /> Share:
      </span>
      <button onClick={copyLink} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-800/50 hover:bg-gray-800 text-sm text-gray-300 transition-colors" title="Copy link">
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        <span>{copied ? 'Copied!' : 'Copy Link'}</span>
      </button>
      <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-800/50 hover:bg-[#1DA1F2]/20 hover:text-[#1DA1F2] text-sm text-gray-300 transition-colors" title="Share on X">
        <Twitter className="w-4 h-4" />
        <span>Post</span>
      </a>
      <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-800/50 hover:bg-[#0A66C2]/20 hover:text-[#0A66C2] text-sm text-gray-300 transition-colors" title="Share on LinkedIn">
        <Linkedin className="w-4 h-4" />
        <span>Share</span>
      </a>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Hero Banner Component
// ─────────────────────────────────────────────────────────────────────────

function HeroBanner({ imageUrl, title }: { imageUrl: string; title: string }) {
  return (
    <div className="relative w-full h-[40vh] min-h-[300px] max-h-[500px] overflow-hidden" role="img" aria-label={title}>
      <img
        src={imageUrl}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/80 to-transparent" />
      <div className="absolute inset-0 bg-black/30" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const InsightPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { initialData } = useSSRData();
  const [post, setPost] = useState<any | null>(initialData?.post || null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>(initialData?.relatedPosts || []);
  const [loading, setLoading] = useState(!initialData?.post);
  const [notFound, setNotFound] = useState(false);
  const progress = useReadingProgress();

  // ── Fetch article ──
  useEffect(() => {
    if (initialData?.post && initialData?.post.slug === slug) {
      // Data already provided by SSR, skip fetch
      return;
    }
    if (!slug) { setNotFound(true); return; }
    fetchPost(slug);
  }, [slug, initialData]);

  async function fetchPost(postSlug: string) {
    setLoading(true);
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', postSlug)
      .eq('is_published', true)
      .single();

    if (error || !data) {
      console.error('Error fetching post:', error);
      setNotFound(true);
    } else {
      setPost(data);
      fetchRelated(data);
    }
    setLoading(false);
  }

  async function fetchRelated(article: any) {
    try {
      // 1. Related by category/tags (RPC)
      const { data: related } = await supabase.rpc('get_related_posts', {
        p_slug: article.slug,
        p_category: article.category || 'Market Insights',
        p_tags: article.tags || [],
        p_limit: 3,
      });

      // 2. Next post (Chronological)
      const { data: next } = await supabase
        .from('blog_posts')
        .select('slug, title, category, cover_image_url, reading_time')
        .eq('is_published', true)
        .gt('published_at', article.published_at)
        .order('published_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      // 3. Previous post (Chronological)
      const { data: prev } = await supabase
        .from('blog_posts')
        .select('slug, title, category, cover_image_url, reading_time')
        .eq('is_published', true)
        .lt('published_at', article.published_at)
        .order('published_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Combine for 3-card grid systemic linking
      const combined = [];
      if (next) combined.push(next);
      if (prev) combined.push(prev);
      
      // Fill remaining slots (up to 3) with related posts that aren't next/prev
      if (related) {
        related.forEach((r: any) => {
          if (combined.length < 3 && !combined.some(c => c.slug === r.slug)) {
            combined.push(r);
          }
        });
      }
      
      setRelatedPosts(combined);
    } catch (err) {
      console.warn('Could not fetch related/navigation posts:', err);
    }
  }

  // ── Derived data
  const override = slug ? SLUG_METADATA_OVERRIDES[slug] : null;

  const displayTitle = override?.title || post?.seo_meta?.title || post?.title || 'Market Insight';
  const displayDesc = override?.description || post?.seo_meta?.description || post?.meta_description || post?.featured_snippet || '';
  const articleText = cleanArticleContent(post?.article_body || post?.content || '');
  const coverImageUrl = post?.cover_image_url || null;
  const canonicalUrl = `https://www.forexfundai.com/insights/${slug}`;
  const tags: string[] = post?.tags || [];
  const toc = post?.toc_json || null;
  const faq = post?.faq_schema || null;
  const uiComponents = post?.ui_components || {};
  const authorProfile = post?.author_profile || null;
  const macroBias = post?.macro_bias || null;
  const confidenceScore = post?.confidence_score || null;

  // Unified data source for institutional components
  const keyLevelsData = post?.key_levels || uiComponents?.key_levels || null;
  const scenarioMatrixData = post?.scenario_matrix || uiComponents?.scenario_matrix || null;
  const mermaidChartsData = post?.mermaid_charts || post?.mermaid_chart || uiComponents?.mermaid_chart || null;

  // Agent 6 v2 manifest — maps [RENDER_COMP_X] placeholders to component definitions
  const articleManifest = post?.react_render_map || post?.components_manifest_map || post?.ui_components || null;

  // Active TOC heading
  const activeHeadingId = useActiveHeading(toc);

  // Determine if sidebar should render
  const hasSidebarContent = useMemo(() => {
    return hasContent(toc) || hasContent(macroBias) || hasContent(tags);
  }, [toc, macroBias, tags]);

  // ── CTA injection logic
  const h2Splitter = '\n## ';
  const parts = articleText.split(h2Splitter);
  let contentBeforeCTA = articleText;
  let contentAfterCTA = '';
  let isCTAMidArticle = false;

  if (parts.length > 3) {
    contentBeforeCTA = parts.slice(0, 3).join(h2Splitter);
    contentAfterCTA = h2Splitter + parts.slice(3).join(h2Splitter);
    isCTAMidArticle = true;
  }

  // ── Render ──
  return (
    <>
      <Helmet>
        <title>{`${displayTitle} — ForexFundAI`}</title>
        <meta name="description" content={displayDesc || 'Institutional market analysis and trading insights from ForexFundAI.'} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${displayTitle} — ForexFundAI`} />
        <meta property="og:description" content={displayDesc || 'Institutional market analysis and trading insights from ForexFundAI.'} />
        {coverImageUrl && <meta property="og:image" content={coverImageUrl} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {post?.published_at && (
          <meta property="article:published_time" content={new Date(post.published_at).toISOString()} />
        )}
        {tags.map((tag: string) => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}
        {hasContent(faq) && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faq.map((f: any) => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            })}
          </script>
        )}
      </Helmet>

      {loading ? (
        <div className="flex justify-center items-center min-h-screen bg-[#0A0D14]">
          <div className="w-full max-w-4xl space-y-4 animate-pulse p-8">
            <div className="h-12 bg-gray-800/60 rounded-xl w-3/4" />
            <div className="h-4 bg-gray-800/40 rounded w-1/2" />
            <div className="flex gap-2 mt-4">
              <div className="h-6 w-20 bg-gray-800/40 rounded-full" />
              <div className="h-6 w-16 bg-gray-800/40 rounded-full" />
            </div>
            <div className="h-[400px] bg-gray-800/30 rounded-2xl mt-8" />
          </div>
        </div>
      ) : notFound ? (
        <Navigate to="/insights" replace />
      ) : (
        <>
          {/* Reading Progress Bar */}
          <ReadingProgressBar progress={progress} />

          <main className="min-h-screen bg-[#0A0D14] pb-24 text-gray-200">
        {/* Cinematic Hero Header */}
        {coverImageUrl && <HeroBanner imageUrl={coverImageUrl} title={displayTitle} />}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 sm:-mt-32 relative z-10">

          <div className="bg-[#0A0D14]/80 backdrop-blur-xl border border-gray-800/50 rounded-3xl p-6 sm:p-10 shadow-2xl mb-12">
            {/* Back Link */}
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-[#D4A843] text-sm mb-6 transition-colors group font-medium"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              Back to Insights
            </Link>

            {/* Tags row */}
            {hasContent(tags) && (
              <div className="flex flex-wrap gap-2 mb-6">
                {tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800/80 text-gray-300 border border-gray-700/50">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.15] tracking-tight mb-6">
              {post?.title || displayTitle}
            </h1>

            {/* Description */}
            {hasContent(displayDesc) && (
              <p className="text-xl text-gray-400 leading-relaxed max-w-4xl mb-8">
                {displayDesc}
              </p>
            )}

            {/* Meta Bar */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
              <ArticleMeta article={post || {}} />
            </div>

            {/* Social Share */}
            <SocialShareBar title={displayTitle} url={canonicalUrl} />
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20 text-sm text-gray-400 mb-12 max-w-4xl mx-auto">
            <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
            <p>
              Trading forex involves substantial risk. This is for educational purposes only.{' '}
              <Link to="/disclaimer" className="text-yellow-500 hover:text-yellow-400 font-medium underline underline-offset-2">
                Read full disclaimer →
              </Link>
            </p>
          </div>

          {/* MAIN LAYOUT */}
          <div className="flex flex-col lg:flex-row gap-12 xl:gap-16">

            {/* Main Column */}
            <div className="flex-1 min-w-0" id="article-body">
              <article>
                {/* Featured Snippet */}
                {hasContent(post?.featured_snippet) && (
                  <blockquote className="p-6 mb-10 rounded-xl bg-gray-900/50 border-l-4 border-[#D4A843] text-xl font-medium text-gray-300 italic">
                    <p>{post.featured_snippet}</p>
                  </blockquote>
                )}

                {/* UI Components */}
                {hasContent(keyLevelsData) && (
                  <div className="mb-10"><KeyLevelsBox levels={keyLevelsData} /></div>
                )}
                {hasContent(mermaidChartsData) && (
                  <div className="mb-10"><MermaidChart chart={mermaidChartsData} caption="Analysis Flowchart" /></div>
                )}
                {hasContent(scenarioMatrixData) && (
                  <div className="mb-10"><ScenarioMatrix scenarios={scenarioMatrixData} /></div>
                )}

                {/* Article Body — Part 1 */}
                <ArticleRenderer content={contentBeforeCTA} manifest={articleManifest} />

                {/* Mid-Article CTA */}
                {isCTAMidArticle && hasContent(macroBias) && (
                  <div className="my-12">
                    <SignalCTABanner
                      bias={macroBias}
                      confidence={confidenceScore ?? 0.5}
                    />
                  </div>
                )}

                {/* Article Body — Part 2 */}
                {hasContent(contentAfterCTA) && (
                  <ArticleRenderer content={contentAfterCTA} manifest={articleManifest} />
                )}

                {/* End-of-article CTA */}
                {!isCTAMidArticle && hasContent(macroBias) && (
                  <div className="my-12">
                    <SignalCTABanner
                      bias={macroBias}
                      confidence={confidenceScore ?? 0.5}
                    />
                  </div>
                )}

                {/* Author Bio */}
                {hasContent(authorProfile) && <div className="mt-16"><AuthorBio author={authorProfile} /></div>}

                {/* FAQ Accordion */}
                {hasContent(faq) && <div className="mt-16"><FAQAccordion faqs={faq} /></div>}
              </article>
            </div>

            {/* Sidebar (Desktop Sticky) */}
            {hasSidebarContent && (
              <aside className="lg:w-[320px] xl:w-[360px] shrink-0">
                <div className="sticky top-24 space-y-8">

                  {/* Table of Contents */}
                  {hasContent(toc) && (
                    <div className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-[#D4A843]" />
                        On this page
                      </h3>
                      <nav className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                        <ul className="space-y-3">
                          {toc.map((item: any) => (
                            <li key={item.id} className={`${item.level > 2 ? 'ml-4' : ''}`}>
                              <a
                                href={`#${item.id}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                                className={`block text-sm transition-colors ${activeHeadingId === item.id ? 'text-[#D4A843] font-medium' : 'text-gray-400 hover:text-gray-200'}`}
                              >
                                {item.title || item.text}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </div>
                  )}

                  {/* Macro Bias Widget */}
                  {hasContent(macroBias) && (
                    <div className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800">
                      <SidebarMacroBiasCard bias={macroBias} confidence={confidenceScore} />
                    </div>
                  )}

                  {/* Sidebar CTA */}
                  <div className="p-6 rounded-2xl bg-gradient-to-b from-[#D4A843]/10 to-transparent border border-[#D4A843]/20">
                    <SidebarCTA />
                  </div>

                </div>
              </aside>
            )}
          </div>

          {/* Related Research Section (Systemic SEO Connectivity via 3-Card Grid) */}
          <div className="mt-24 pt-12 border-t border-gray-800">
            <RelatedPosts posts={relatedPosts} />
          </div>

        </div>
      </main>
    </>
  )}
</>
);
};

export default InsightPost;
